/** Filter state of the participants table + labels for the active-filter chips */

export type ParticipantTabFilters = {
  type?: 'MINISTER' | 'DELEGATE' | 'GUEST' | 'ALL';
  search?: string;
  payment_status?: 'PENDING' | 'PARTIAL' | 'PAID' | 'REVERSED';
  registration_status?: 'PRE_REGISTERED' | 'VERIFIED' | 'REJECTED';
  attendance_status?: 'PENDING' | 'ATTENDED' | 'ABSENT';
  is_mandatory?: boolean;
  district_id?: string;
  region_id?: string;
  /** Client-side: participants with a recorded payment that still has no receipt */
  missing_receipt?: boolean;
  /** Client-side: Digital-signature registrations still without their signature (ADB-169) */
  missing_signature?: boolean;
};

export const TYPE_LABELS = {
  MINISTER: 'Ministros',
  DELEGATE: 'Delegados',
  GUEST: 'Invitados',
} as const;

export const ATTENDANCE_LABELS = {
  ATTENDED: 'Asistieron',
  PENDING: 'Asistencia pendiente',
  ABSENT: 'Ausentes',
} as const;

export interface FilterChip {
  key: string;
  label: string;
  /** Filter values to reset when the chip is removed */
  clear: Partial<ParticipantTabFilters>;
}

/**
 * Chips for the filters tucked in "Más filtros" (Inscripción and Pago have visible selects).
 * Removing a district also removes its region.
 */
export function activeFilterChips(
  filters: ParticipantTabFilters,
  names: { district?: string; region?: string },
): FilterChip[] {
  const chips: FilterChip[] = [];
  if (filters.type && filters.type !== 'ALL') {
    chips.push({ key: 'type', label: TYPE_LABELS[filters.type], clear: { type: 'ALL' } });
  }
  if (filters.attendance_status) {
    chips.push({ key: 'attendance', label: ATTENDANCE_LABELS[filters.attendance_status], clear: { attendance_status: undefined } });
  }
  if (filters.is_mandatory !== undefined) {
    chips.push({
      key: 'mandatory',
      label: filters.is_mandatory ? 'Obligatorios' : 'Opcionales',
      clear: { is_mandatory: undefined },
    });
  }
  if (filters.missing_receipt) {
    chips.push({ key: 'missing_receipt', label: 'Pagos sin recibo', clear: { missing_receipt: undefined } });
  }
  if (filters.missing_signature) {
    chips.push({ key: 'missing_signature', label: 'Sin firma', clear: { missing_signature: undefined } });
  }
  if (filters.district_id) {
    chips.push({
      key: 'district',
      label: `Distrito: ${names.district ?? '…'}`,
      clear: { district_id: undefined, region_id: undefined },
    });
  }
  if (filters.region_id) {
    chips.push({ key: 'region', label: `Región: ${names.region ?? '…'}`, clear: { region_id: undefined } });
  }
  return chips;
}

/** Columns the user can hide from the "Columnas" menu (name, selection and actions are always shown) */
export const TOGGLEABLE_COLUMNS: { id: string; label: string; defaultVisible: boolean }[] = [
  { id: 'location', label: 'Distrito / Región', defaultVisible: true },
  { id: 'price', label: 'Precio', defaultVisible: true },
  { id: 'registration_status', label: 'Inscripción', defaultVisible: true },
  { id: 'payment_status', label: 'Pago', defaultVisible: true },
  { id: 'attendance', label: 'Asistencia', defaultVisible: true },
  { id: 'registration_date', label: 'Fecha de registro', defaultVisible: false },
];
